import axios from 'axios';
import API_CONFIG from '../config/api';
import { message } from 'antd';

// Sử dụng dynamic API URL
const getAPIBaseURL = () => {
  return API_CONFIG.CURRENT_URL;
};

// Variable để track refresh token process
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Tạo axios instance
const apiClient = axios.create({
  baseURL: getAPIBaseURL(),
  timeout: 30000,
});

// Request interceptor để thêm token vào header
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    //console.log('📤 API Request DEBUG [MAIN INTERCEPTOR]:', {
    //   url: config.url,
    //   method: config.method,
    //   hasAuth: !!config.headers.Authorization,
    //   authToken: config.headers.Authorization ? config.headers.Authorization.substring(0, 30) + '...' : null,
    //   accessTokenParam: config.params?.accessToken ? config.params.accessToken.substring(0, 20) + '...' : null,
    //   storageToken: accessToken ? accessToken.substring(0, 20) + '...' : null,
    //   isRetry: !!config._retry,
    //   interceptorId: 'MAIN'
    // });
    
    return config;
  },
  (error) => {
    //console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor để xử lý token refresh
apiClient.interceptors.response.use(
  (response) => {
    //console.log('✅ API Response OK:', {
    //   url: response.config?.url,
    //   status: response.status,
    //   hasData: !!response.data
    // });
    return response;
  },
  async (error) => {
    //console.log('� INTERCEPTOR TRIGGERED! 🔥');
    //console.log('�🚨 API Response Error:', {
    //   url: error.config?.url,
    //   status: error.response?.status,
    //   message: error.message,
    //   responseData: error.response?.data
    // });
    
    // Kiểm tra xem có phải lỗi token không
    const isTokenError = error.response?.data?.message?.includes('Token không hợp lệ') || 
                        error.response?.data?.message?.includes('đã hết hạn') ||
                        error.response?.data?.message?.includes('Token hết hạn') ||
                        error.response?.data?.message?.includes('không hợp lệ') ||
                        error.response?.status === 401;
    
    //console.log('🔍 Token error check:', {
    //   isTokenError,
    //   status: error.response?.status,
    //   message: error.response?.data?.message
    // });
    
    const originalRequest = error.config;

    // Nếu là lỗi token (401 hoặc message chứa "token không hợp lệ") và chưa retry
    if (isTokenError && originalRequest && !originalRequest._retry) {
      //console.log('🚨 Token error detected - attempting token refresh...');
      
      const refreshToken = localStorage.getItem('refreshToken');
      const accessToken = localStorage.getItem('accessToken');
      
      //console.log('🔍 Current tokens:', {
      //   hasRefreshToken: !!refreshToken,
      //   hasAccessToken: !!accessToken,
      //   refreshTokenPreview: refreshToken ? refreshToken.substring(0, 20) + '...' : null
      // });
      
      if (!refreshToken) {
        //console.error('❌ No refresh token found - redirecting to login');
        localStorage.clear();
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Đánh dấu request đã retry để tránh infinite loop
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        
        try {
          //console.log('📡 Calling refresh token API...');
          
          const refreshResponse = await axios.post(`${getAPIBaseURL()}/api/auth/refresh-token`, {
            refreshToken: refreshToken
          });
          
          //console.log('✅ Refresh response:', refreshResponse.data);
          
          if (refreshResponse.data.success && refreshResponse.data.data) {
            const tokenData = refreshResponse.data.data;
            const newAccessToken = tokenData.access_token;
            const newRefreshToken = tokenData.refresh_token;
            
            //console.log('🔄 Updating tokens in localStorage...');
            
            // Cập nhật tokens
            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            
            //console.log('🔍 Before retry - originalRequest details:', {
            //   url: originalRequest.url,
            //   method: originalRequest.method,
            //   hasHeaders: !!originalRequest.headers,
            //   currentAuth: originalRequest.headers?.Authorization?.substring(0, 30) + '...',
            //   hasParams: !!originalRequest.params,
            //   accessTokenParam: originalRequest.params?.accessToken?.substring(0, 20) + '...'
            // });
            
            // Đảm bảo headers tồn tại
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            
            // Cập nhật header Authorization cho request gốc
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            
            // Cập nhật query params nếu có accessToken
            if (originalRequest.params && originalRequest.params.accessToken) {
              originalRequest.params.accessToken = newAccessToken;
            }
            
            //console.log('🔍 After update - originalRequest details:', {
            //   url: originalRequest.url,
            //   method: originalRequest.method,
            //   newAuth: originalRequest.headers?.Authorization?.substring(0, 30) + '...',
            //   newAccessTokenParam: originalRequest.params?.accessToken?.substring(0, 20) + '...'
            // });
            
            isRefreshing = false;
            
            // Process tất cả requests đang chờ
            processQueue(null, newAccessToken);
            
            //console.log('🔄 Executing retry request NOW...');
            
            try {
              // Debug: In ra token thực tế sẽ được gửi
              //console.log('🔍 FINAL REQUEST CHECK before retry:', {
              //   url: originalRequest.url,
              //   method: originalRequest.method,
              //   authHeader: originalRequest.headers?.Authorization,
              //   accessTokenParam: originalRequest.params?.accessToken,
              //   newTokenFromStorage: localStorage.getItem('accessToken')?.substring(0, 20) + '...'
              // });
              
              // Refresh token thành công, bắt đầu retry process
              //console.log('🧪 Testing new token with simple API call first...');
              
              // Tạo axios instance mới để bypass interceptor
              const testAxios = axios.create({
                baseURL: getAPIBaseURL(),
                timeout: 10000,
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${newAccessToken}`
                }
              });
              
              let retryResponse;
              
              // Test token mới với API categories trước
              try {
                const testResponse = await testAxios.get('/api/categories/', {
                  params: { accessToken: newAccessToken }
                });
                //console.log('✅ Token test successful! New token works with categories API');
                
                // Nếu test thành công, thử original request với format đúng
                //console.log('🔄 Now trying original request with tested token...');
                
                // Kiểm tra nếu là các API cần accessToken trong body
                const apisNeedTokenInBody = [
                  '/api/products/add-json',
                  '/api/products/images/',
                  '/api/products/variants/',
                  '/api/categories/add',
                  '/api/categories/',
                  '/api/attributes',
                  '/api/stock/',
                  '/api/export/',
                  '/api/import/',
                  '/api/invoices/import',
                  '/api/news',
                  '/api/loaisukien',
                  '/api/sukien',
                  '/api/auth/get-pending-nhomdaily',
                  '/api/auth/approve-employee',
                  '/api/auth/reject-employee',
                  '/api/auth/create-export-invoice-for-management',
                  '/api/auth/get-eligible-invoices-with-nhomdaily'
                ];
                
                const needsTokenInBody = apisNeedTokenInBody.some(apiPath => 
                  originalRequest.url.includes(apiPath)
                ) || originalRequest.url.match(/\/api\/products\/\d+\/(simple|images|variants)/) ||
                   originalRequest.url.match(/\/api\/categories\/\d+$/) && originalRequest.method.toLowerCase() === 'put' ||
                   originalRequest.url.match(/\/api\/attributes/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase()) ||
                   originalRequest.url.match(/\/api\/(stock|export|import)/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase()) ||
                   originalRequest.url.match(/\/api\/invoices\/import/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase()) ||
                   originalRequest.url.match(/\/api\/news/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase());
                
                if (needsTokenInBody) {
                  //console.log('🎯 API requires accessToken in request body - updating token');
                  
                  let requestData;
                  if (originalRequest.data) {
                    requestData = typeof originalRequest.data === 'string' 
                      ? JSON.parse(originalRequest.data) 
                      : originalRequest.data;
                  } else {
                    requestData = {};
                  }
                  
                  requestData.accessToken = newAccessToken;
                  
                  retryResponse = await testAxios({
                    url: originalRequest.url,
                    method: originalRequest.method,
                    data: requestData,
                    params: originalRequest.params
                  });
                } else {
                  // Cho các API khác chỉ cần token trong header hoặc query params
                  const retryParams = originalRequest.params ? { ...originalRequest.params } : {};
                  
                  // Nếu là GET request cần token trong params
                  if (originalRequest.method.toLowerCase() === 'get' && 
                      (originalRequest.url.includes('/api/categories/') || 
                       originalRequest.url.includes('/api/attributes/') ||
                       originalRequest.url.includes('/api/stock/') ||
                       originalRequest.url.includes('/api/export/') ||
                       originalRequest.url.includes('/api/import/') ||
                       originalRequest.url.includes('/api/customers/'))) {
                    retryParams.accessToken = newAccessToken;
                  }
                  
                  retryResponse = await testAxios({
                    url: originalRequest.url,
                    method: originalRequest.method,
                    data: originalRequest.data,
                    params: retryParams
                  });
                }
                
                //console.log('✅ RETRY SUCCESSFUL! Response:', {
                //   status: retryResponse.status,
                //   hasData: !!retryResponse.data
                // });
                
              } catch (testError) {
                //console.error('❌ TOKEN TEST FAILED:', {
                //   status: testError.response?.status,
                //   message: testError.response?.data?.message,
                //   tokenUsed: `Bearer ${newAccessToken}`.substring(0, 30) + '...'
                // });
                
                // Nếu test fail, có thể backend chưa sync
                //console.log('⏰ Token test failed, trying longer delay...');
                await new Promise(resolve => setTimeout(resolve, 3000));
                
                // Thử lại với delay dài hơn và format đúng
                const apisNeedTokenInBody = [
                  '/api/products/add-json',
                  '/api/products/images/',
                  '/api/products/variants/',
                  '/api/categories/add',
                  '/api/categories/',
                  '/api/attributes',
                  '/api/stock/',
                  '/api/export/',
                  '/api/import/',
                  '/api/invoices/import',
                  '/api/news',
                  '/api/loaisukien',
                  '/api/sukien',
                  '/api/auth/get-pending-nhomdaily',
                  '/api/auth/approve-employee',
                  '/api/auth/reject-employee',
                  '/api/auth/create-export-invoice-for-management',
                  '/api/auth/get-eligible-invoices-with-nhomdaily'
                ];
                
                const needsTokenInBody = apisNeedTokenInBody.some(apiPath => 
                  originalRequest.url.includes(apiPath)
                ) || originalRequest.url.match(/\/api\/products\/\d+\/(simple|images|variants)/) ||
                   originalRequest.url.match(/\/api\/categories\/\d+$/) && originalRequest.method.toLowerCase() === 'put' ||
                   originalRequest.url.match(/\/api\/attributes/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase()) ||
                   originalRequest.url.match(/\/api\/(stock|export|import)/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase()) ||
                   originalRequest.url.match(/\/api\/invoices\/import/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase()) ||
                   originalRequest.url.match(/\/api\/news/) && ['post', 'put', 'delete'].includes(originalRequest.method.toLowerCase());
                
                if (needsTokenInBody) {
                  let requestData;
                  if (originalRequest.data) {
                    requestData = typeof originalRequest.data === 'string' 
                      ? JSON.parse(originalRequest.data) 
                      : originalRequest.data;
                  } else {
                    requestData = {};
                  }
                  
                  requestData.accessToken = newAccessToken;
                  
                  retryResponse = await testAxios({
                    url: originalRequest.url,
                    method: originalRequest.method,
                    data: requestData,
                    params: originalRequest.params
                  });
                } else {
                  const retryParams = originalRequest.params ? { ...originalRequest.params } : {};
                  
                  // Nếu là GET request cần token trong params
                  if (originalRequest.method.toLowerCase() === 'get' && 
                      (originalRequest.url.includes('/api/categories/') || 
                       originalRequest.url.includes('/api/attributes/') ||
                       originalRequest.url.includes('/api/stock/') ||
                       originalRequest.url.includes('/api/export/') ||
                       originalRequest.url.includes('/api/import/') ||
                       originalRequest.url.includes('/api/customers/'))) {
                    retryParams.accessToken = newAccessToken;
                  }
                  
                  retryResponse = await testAxios({
                    url: originalRequest.url,
                    method: originalRequest.method,
                    data: originalRequest.data,
                    params: retryParams
                  });
                }
              }
              //console.log('✅ RETRY SUCCESSFUL! Response:', {
              //   status: retryResponse.status,
              //   hasData: !!retryResponse.data,
              //   url: retryResponse.config?.url
              // });
              return retryResponse;
            } catch (retryError) {
              //console.error('❌ RETRY FAILED after successful refresh:', retryError);
              //console.error('❌ Retry error details:', {
              //   status: retryError.response?.status,
              //   message: retryError.response?.data?.message,
              //   url: retryError.config?.url,
              //   requestHeaders: retryError.config?.headers,
              //   requestParams: retryError.config?.params
              // });
              
              //console.log('⚠️ Refresh was successful but retry failed - NOT redirecting to login');
              
              // Nếu retry fail, KHÔNG redirect về login vì refresh đã thành công
              // Chỉ reject error để user thấy lỗi API thực tế
              return Promise.reject(retryError);
            }
            
          } else {
            //console.error('❌ Invalid refresh response format');
            isRefreshing = false;
            processQueue(new Error('Invalid refresh response'), null);
            
            // Clear tokens và redirect
            localStorage.clear();
            message.error('Phản hồi refresh token không hợp lệ. Vui lòng đăng nhập lại.');
            window.location.href = '/login';
            return Promise.reject(error);
          }
          
        } catch (refreshError) {
          //console.error('❌ REFRESH TOKEN API FAILED:', refreshError);
          //console.error('❌ Refresh error details:', {
          //   status: refreshError.response?.status,
          //   message: refreshError.response?.data?.message,
          //   url: refreshError.config?.url
          // });
          
          isRefreshing = false;
          processQueue(refreshError, null);
          
          // Clear tất cả tokens chỉ khi refresh thật sự thất bại
          localStorage.clear();
          
          // Hiển thị thông báo lỗi
          if (refreshError.response?.status === 401) {
            message.error('Refresh token đã hết hạn. Vui lòng đăng nhập lại.');
          } else {
            message.error('Không thể làm mới phiên đăng nhập. Vui lòng đăng nhập lại.');
          }
          
          //console.log('🚪 Redirecting to login page due to REFRESH failure...');
          
          // Redirect về login chỉ khi refresh thất bại
          window.location.href = '/login';
          
          return Promise.reject(refreshError);
        }
      } else {
        // Nếu đang refresh, thêm vào queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          if (originalRequest.params && originalRequest.params.accessToken) {
            originalRequest.params.accessToken = token;
          }
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;